/**
 * Yakıt Yönetimi metinleri (ADR 0117; ortağın tasarımı `docs/gereksinimler/ortak-2026-10-23-yakit-mrv/`
 * 01–07). Anahtarlar API sözleşmesinin değerleriyle (görev, konum, yöntem, kategori, faktör sınıfı,
 * hata kodu) eşleşir. Ekran adları İngilizce, Türkçe ikinci dil; tasarım Türkçedir.
 */

import {
  fuelConsumptionEn,
  fuelConsumptionErrorsEn,
  fuelConsumptionErrorsTr,
  fuelConsumptionFieldErrorsEn,
  fuelConsumptionFieldErrorsTr,
  fuelConsumptionTr,
} from './fuel-consumption.js';
import {
  fuelLedgerEn,
  fuelLedgerErrorsEn,
  fuelLedgerErrorsTr,
  fuelLedgerFieldErrorsEn,
  fuelLedgerFieldErrorsTr,
  fuelLedgerTr,
} from './fuel-ledger.js';
import {
  fuelPeriodEn,
  fuelPeriodErrorsEn,
  fuelPeriodErrorsTr,
  fuelPeriodTr,
} from './fuel-period.js';
import {
  fuelReconciliationErrorsEn,
  fuelReconciliationErrorsTr,
  fuelRobEn,
  fuelRobTr,
  fuelVoyageAnalysisEn,
  fuelVoyageAnalysisTr,
} from './fuel-reconciliation.js';

export const fuelEn = {
  title: 'Fuel Management',
  description: 'Tanks, bunkering, transfers, consumption, ROB reconciliation and voyage analysis.',
  tabsLabel: 'Fuel management sections',
  tabs: {
    overview: 'Overview',
    tanks: 'Tanks',
    bunkering: 'Bunkering',
    transfers: 'Transfers',
    consumption: 'Consumption',
    rob: 'ROB Reconciliation',
    'voyage-analysis': 'Voyage Analysis',
  },
  comingSoon: {
    title: '{{tab}} is being prepared',
    body: 'This section comes with stage {{stage}} of the fuel management plan.',
    overview:
      'ROB by fuel, tank levels, daily consumption, recent movements and ROB reconciliation.',
    bunkering:
      'Bunker delivery: BDN, batch, quality, BDN / ship measurement / accepted quantity and tank distribution.',
    transfers:
      'Tank transfer, discharge, return and fuel change; the vessel total ROB does not change on a transfer.',
    consumption:
      'Consumption by equipment and fuel, entered once in the voyage report (Noon, Departure, Arrival …).',
    rob: 'Tank soundings compared with the calculated ROB; differences go through an adjustment request.',
    'voyage-analysis':
      'Operation and cost analysis by voyage, leg and charter party; MRV and ETS results.',
  },
  vessel: 'Vessel',
  tanks: {
    subtabsLabel: 'Tanks sections',
    definitions: 'Tank Definitions',
    fuelTypes: 'Fuel Types',
    add: 'Add New Tank',
    search: 'Search by tank name or code...',
    searchLabel: 'Search tanks',
    listLabel: 'Tanks',
    empty: 'No tanks yet.',
    emptyHint: 'Add the vessel’s fuel tanks with “Add New Tank”.',
    emptyOffice: 'The vessel has not defined its tanks yet.',
    noMatch: 'No tank matches the search.',
    inactive: 'Inactive',
    info: 'Tank Information',
    newTank: 'New Tank',
    fields: {
      code: 'Tank Code',
      name: 'Tank Name',
      role: 'Tank Role',
      location: 'Location',
      allowedFuelTypeIds: 'Allowed Fuel Types',
      consumerIds: 'Connected Consumers',
      description: 'Description',
      capacityM3: 'Capacity (m³)',
      fillLimitPct: 'Fill Limit (%)',
      unusableM3: 'Unusable Volume (m³)',
      lowLevel: 'Low Level Alarm ({{unit}})',
      highLevel: 'High Level Alarm ({{unit}})',
      levelUnit: 'Level alarm unit',
      measurementMethod: 'Measurement Method',
      calibration: 'Calibration Table',
      trimList: 'Trim/List Table',
      active: 'Active',
    },
    select: 'Select',
    roles: {
      storage: 'Storage',
      settling: 'Settling',
      service: 'Service',
      overflow: 'Overflow',
      other: 'Other',
    },
    locations: { port: 'Port', starboard: 'Starboard', centre: 'Centre' },
    methods: { sounding: 'Sounding', ullage: 'Ullage', sensor: 'Sensor' },
    units: { pct: '%', m3: 'm³' },
    file: {
      upload: 'Upload file',
      remove: 'Remove {{name}}',
      none: 'No file uploaded',
      hint: 'PDF, JPG or PNG, at most 25 MB',
      pending: 'Saved with the tank',
      removed: 'Removed when you save',
      version: 'Version {{version}}',
    },
    activeOn: 'Active',
    activeOff: 'Out of use',
    openingBalance: 'Opening Balance',
    openingBalanceSoon: 'The opening balance comes with the ROB ledger (stage Y2).',
    cancel: 'Cancel',
    save: 'Save',
    saved: 'Tank saved.',
    readOnly: 'Tanks are vessel data: the vessel defines them, the office views them read-only.',
    noFuelTypes: 'No fuel types yet. The office adds them under “Fuel Types”.',
    operating: 'Operating capacity {{value}} m³',
  },
  fuelTypes: {
    add: 'Add Fuel Type',
    title: 'Fuel Type',
    listLabel: 'Fuel types',
    empty: 'No fuel types yet.',
    emptyHint: 'Add the fuels the fleet uses, e.g. VLSFO, MGO.',
    shipNote:
      'Fuel types are company data: the office maintains them and they arrive on board with synchronisation.',
    columns: {
      code: 'Code',
      name: 'Name',
      category: 'Category',
      grade: 'Grade',
      factorClass: 'Emission Factor Class',
      status: 'Status',
    },
    fields: {
      code: 'Code',
      name: 'Name',
      category: 'Category',
      grade: 'Grade',
      factorClass: 'Emission Factor Class',
      factorClassHint:
        'The trade name does not decide the emission factor. Leave empty until the class is verified against the monitoring plan.',
      active: 'Active',
      remarks: 'Remarks',
    },
    categories: {
      residual: 'Residual',
      distillate: 'Distillate',
      lng: 'LNG',
      lpg: 'LPG',
      methanol: 'Methanol',
      ethanol: 'Ethanol',
      biofuel: 'Biofuel',
      other: 'Other',
    },
    factorClasses: {
      HFO: 'HFO',
      LFO: 'LFO',
      MDO_MGO: 'MDO / MGO',
      LNG_OTTO_MS: 'LNG Otto (medium speed)',
      LNG_OTTO_SS: 'LNG Otto (slow speed)',
      LNG_DIESEL: 'LNG Diesel',
      LPG_PROPANE: 'LPG (propane)',
      LPG_BUTANE: 'LPG (butane)',
      METHANOL: 'Methanol',
      ETHANOL: 'Ethanol',
      OTHER: 'Other',
    },
    unverified: 'Not verified',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit {{code}}',
    close: 'Close',
    saved: 'Fuel type saved.',
  },
  errors: {
    ...fuelLedgerErrorsEn,
    ...fuelConsumptionErrorsEn,
    ...fuelReconciliationErrorsEn,
    ...fuelPeriodErrorsEn,
    GENERIC: 'Something went wrong. Try again.',
    FORBIDDEN: 'You do not have permission for this.',
    NOT_FOUND: 'The record was not found. It may have been removed.',
    VALIDATION_FAILED: 'Some fields are not valid.',
    ROW_VERSION_CONFLICT: 'Someone changed this record meanwhile. Reload and try again.',
    SHIP_IS_WRITER: 'Tanks are kept on board once the vessel is live.',
    OFFICE_IS_WRITER: 'Fuel types are maintained in the office.',
    INVALID_FUEL_TANK: 'Some tank fields are not valid.',
    INVALID_FUEL_TYPE: 'Some fuel type fields are not valid.',
    DUPLICATE_TANK_CODE: 'Another tank on this vessel already uses this code.',
    DUPLICATE_FUEL_TYPE_CODE: 'Another fuel type already uses this code.',
    FUEL_TYPE_CODE_FIXED: 'The code of a fuel type cannot be changed.',
    UNKNOWN_FUEL_TYPE: 'An allowed fuel type is no longer in the fuel list.',
    UNKNOWN_EQUIPMENT: 'A connected consumer is no longer in the equipment list.',
    TANK_HAS_MOVEMENTS: 'A tank with fuel movements cannot be removed; set it out of use instead.',
    INVALID_TANK_TABLE: 'The table file is not valid.',
    FILE_NOT_ON_BOARD: 'The file did not reach the ship server. Upload it again.',
    FILE_TOO_LARGE: 'The file is larger than 25 MB.',
    UNSUPPORTED_FILE_TYPE: 'Only PDF, JPG and PNG files can be uploaded.',
  },
  fieldErrors: {
    ...fuelLedgerFieldErrorsEn,
    ...fuelConsumptionFieldErrorsEn,
    REQUIRED: 'Required.',
    TOO_LONG: 'Too long.',
    INVALID_TEXT: 'Enter text.',
    INVALID_OPTION: 'Choose an option.',
    INVALID_CODE: 'Use capital letters, digits and - . _ / only.',
    INVALID_DECIMAL: 'Enter a number (at most 12 digits before and after the decimal point).',
    NEGATIVE_NOT_ALLOWED: 'The value cannot be negative.',
    OUT_OF_RANGE: 'The value is out of range.',
    UNUSABLE_EXCEEDS_CAPACITY: 'Must be smaller than the capacity.',
    LEVEL_ORDER: 'Must be greater than the low level alarm.',
    DUPLICATE_VALUE: 'Each item can be chosen once.',
    INVALID_LIST: 'Choose from the list.',
    INVALID_ID: 'Choose from the list.',
    DUPLICATE: 'Already in use.',
  },
  ...fuelLedgerEn,
  consumption: fuelConsumptionEn,
  rob: fuelRobEn,
  period: fuelPeriodEn,
  analysis: fuelVoyageAnalysisEn,
};

export const fuelTr: typeof fuelEn = {
  title: 'Yakıt Yönetimi',
  description: 'Tanklar, yakıt alımları, transferler, tüketim, ROB mutabakatı ve sefer analizi.',
  tabsLabel: 'Yakıt yönetimi bölümleri',
  tabs: {
    overview: 'Genel Bakış',
    tanks: 'Tanklar',
    bunkering: 'Yakıt Alımları',
    transfers: 'Transferler',
    consumption: 'Tüketim',
    rob: 'ROB Mutabakatı',
    'voyage-analysis': 'Voyage Analizi',
  },
  comingSoon: {
    title: '{{tab}} hazırlanıyor',
    body: 'Bu bölüm yakıt yönetimi planının {{stage}} aşamasıyla gelir.',
    overview: 'Yakıt bazında ROB, tank doluluğu, günlük tüketim, son hareketler ve ROB mutabakatı.',
    bunkering:
      'Yakıt alımı: BDN, parti, kalite, BDN / gemi ölçümü / kabul miktarı ve tank dağılımı.',
    transfers:
      'Tank transferi, boşaltma, iade ve yakıt değişimi; transfer geminin toplam ROB’unu değiştirmez.',
    consumption:
      'Ekipman ve yakıt bazında tüketim; sefer raporunda (Noon, Departure, Arrival …) bir kez girilir.',
    rob: 'Tank ölçümleri hesaplanan ROB ile karşılaştırılır; fark düzeltme talebiyle işlenir.',
    'voyage-analysis':
      'Voyage, leg ve charter party bazında operasyon ve maliyet analizi; MRV ve ETS sonuçları.',
  },
  vessel: 'Gemi',
  tanks: {
    subtabsLabel: 'Tank bölümleri',
    definitions: 'Tank Tanımları',
    fuelTypes: 'Yakıt Türleri',
    add: 'Yeni Tank Ekle',
    search: 'Tank adı veya kodu ile ara...',
    searchLabel: 'Tank ara',
    listLabel: 'Tanklar',
    empty: 'Henüz tank yok.',
    emptyHint: 'Geminin yakıt tanklarını “Yeni Tank Ekle” ile ekleyin.',
    emptyOffice: 'Gemi tanklarını henüz tanımlamadı.',
    noMatch: 'Aramaya uyan tank yok.',
    inactive: 'Pasif',
    info: 'Tank Bilgileri',
    newTank: 'Yeni Tank',
    fields: {
      code: 'Tank Kodu',
      name: 'Tank Adı',
      role: 'Tank Görevi',
      location: 'Konum',
      allowedFuelTypeIds: 'İzin Verilen Yakıt Türleri',
      consumerIds: 'Bağlı Tüketiciler',
      description: 'Açıklama',
      capacityM3: 'Kapasite (m³)',
      fillLimitPct: 'Dolum Sınırı (%)',
      unusableM3: 'Kullanılamayan Hacim (m³)',
      lowLevel: 'Düşük Seviye Alarmı ({{unit}})',
      highLevel: 'Yüksek Seviye Alarmı ({{unit}})',
      levelUnit: 'Seviye alarmı birimi',
      measurementMethod: 'Ölçüm Yöntemi',
      calibration: 'Kalibrasyon Tablosu',
      trimList: 'Trim/List Tablosu',
      active: 'Aktif',
    },
    select: 'Seçin',
    roles: {
      storage: 'Storage',
      settling: 'Settling',
      service: 'Service',
      overflow: 'Overflow',
      other: 'Diğer',
    },
    locations: { port: 'Port', starboard: 'Starboard', centre: 'Centre' },
    methods: { sounding: 'Sounding', ullage: 'Ullage', sensor: 'Sensör' },
    units: { pct: '%', m3: 'm³' },
    file: {
      upload: 'Dosya yükle',
      remove: '{{name}} kaldır',
      none: 'Dosya yüklenmedi',
      hint: 'PDF, JPG ya da PNG, en çok 25 MB',
      pending: 'Tankla birlikte kaydedilir',
      removed: 'Kaydedince kaldırılır',
      version: 'Sürüm {{version}}',
    },
    activeOn: 'Aktif',
    activeOff: 'Kullanım dışı',
    openingBalance: 'Açılış Bakiyesi',
    openingBalanceSoon: 'Açılış bakiyesi ROB defteriyle gelir (Y2 aşaması).',
    cancel: 'İptal',
    save: 'Kaydet',
    saved: 'Tank kaydedildi.',
    readOnly: 'Tanklar gemi verisidir: gemi tanımlar, ofis salt okunur görür.',
    noFuelTypes: 'Henüz yakıt türü yok. Ofis “Yakıt Türleri”nde ekler.',
    operating: 'İşletme kapasitesi {{value}} m³',
  },
  fuelTypes: {
    add: 'Yakıt Türü Ekle',
    title: 'Yakıt Türü',
    listLabel: 'Yakıt türleri',
    empty: 'Henüz yakıt türü yok.',
    emptyHint: 'Filonun kullandığı yakıtları ekleyin (ör. VLSFO, MGO).',
    shipNote: 'Yakıt türleri şirket verisidir: ofis yönetir, senkronla gemiye iner.',
    columns: {
      code: 'Kod',
      name: 'Ad',
      category: 'Kategori',
      grade: 'Grade',
      factorClass: 'Emisyon Faktör Sınıfı',
      status: 'Durum',
    },
    fields: {
      code: 'Kod',
      name: 'Ad',
      category: 'Kategori',
      grade: 'Grade',
      factorClass: 'Emisyon Faktör Sınıfı',
      factorClassHint:
        'Ticari ad emisyon faktörünü belirlemez. Sınıf izleme planıyla doğrulanana kadar boş bırakın.',
      active: 'Aktif',
      remarks: 'Not',
    },
    categories: {
      residual: 'Residual',
      distillate: 'Distillate',
      lng: 'LNG',
      lpg: 'LPG',
      methanol: 'Metanol',
      ethanol: 'Etanol',
      biofuel: 'Biyoyakıt',
      other: 'Diğer',
    },
    factorClasses: {
      HFO: 'HFO',
      LFO: 'LFO',
      MDO_MGO: 'MDO / MGO',
      LNG_OTTO_MS: 'LNG Otto (orta devir)',
      LNG_OTTO_SS: 'LNG Otto (yavaş devir)',
      LNG_DIESEL: 'LNG Diesel',
      LPG_PROPANE: 'LPG (propan)',
      LPG_BUTANE: 'LPG (bütan)',
      METHANOL: 'Metanol',
      ETHANOL: 'Etanol',
      OTHER: 'Diğer',
    },
    unverified: 'Doğrulanmadı',
    active: 'Aktif',
    inactive: 'Pasif',
    edit: '{{code}} düzenle',
    close: 'Kapat',
    saved: 'Yakıt türü kaydedildi.',
  },
  errors: {
    ...fuelLedgerErrorsTr,
    ...fuelConsumptionErrorsTr,
    ...fuelReconciliationErrorsTr,
    ...fuelPeriodErrorsTr,
    GENERIC: 'Bir sorun oluştu. Yeniden deneyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Kayıt bulunamadı. Kaldırılmış olabilir.',
    VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
    ROW_VERSION_CONFLICT: 'Kaydı bu arada başkası değiştirdi. Yenileyip yeniden deneyin.',
    SHIP_IS_WRITER: 'Gemi etkinleştikten sonra tanklar gemide tutulur.',
    OFFICE_IS_WRITER: 'Yakıt türleri ofiste yönetilir.',
    INVALID_FUEL_TANK: 'Bazı tank alanları geçersiz.',
    INVALID_FUEL_TYPE: 'Bazı yakıt türü alanları geçersiz.',
    DUPLICATE_TANK_CODE: 'Bu gemide aynı kodu kullanan başka bir tank var.',
    DUPLICATE_FUEL_TYPE_CODE: 'Aynı kodu kullanan başka bir yakıt türü var.',
    FUEL_TYPE_CODE_FIXED: 'Yakıt türünün kodu değiştirilemez.',
    UNKNOWN_FUEL_TYPE: 'İzin verilen bir yakıt türü artık yakıt listesinde yok.',
    UNKNOWN_EQUIPMENT: 'Bağlı bir tüketici artık ekipman listesinde yok.',
    TANK_HAS_MOVEMENTS:
      'Yakıt hareketi olan tank kaldırılamaz; bunun yerine kullanım dışı bırakın.',
    INVALID_TANK_TABLE: 'Tablo dosyası geçersiz.',
    FILE_NOT_ON_BOARD: 'Dosya gemi sunucusuna ulaşmadı. Yeniden yükleyin.',
    FILE_TOO_LARGE: 'Dosya 25 MB’tan büyük.',
    UNSUPPORTED_FILE_TYPE: 'Yalnız PDF, JPG ve PNG dosyaları yüklenebilir.',
  },
  fieldErrors: {
    ...fuelLedgerFieldErrorsTr,
    ...fuelConsumptionFieldErrorsTr,
    REQUIRED: 'Zorunlu.',
    TOO_LONG: 'Çok uzun.',
    INVALID_TEXT: 'Metin girin.',
    INVALID_OPTION: 'Bir seçenek seçin.',
    INVALID_CODE: 'Yalnız büyük harf, rakam ve - . _ / kullanın.',
    INVALID_DECIMAL: 'Sayı girin (ondalık ayırıcının önünde ve arkasında en çok 12 hane).',
    NEGATIVE_NOT_ALLOWED: 'Değer eksi olamaz.',
    OUT_OF_RANGE: 'Değer aralık dışında.',
    UNUSABLE_EXCEEDS_CAPACITY: 'Kapasiteden küçük olmalı.',
    LEVEL_ORDER: 'Düşük seviye alarmından büyük olmalı.',
    DUPLICATE_VALUE: 'Her öğe bir kez seçilir.',
    INVALID_LIST: 'Listeden seçin.',
    INVALID_ID: 'Listeden seçin.',
    DUPLICATE: 'Zaten kullanılıyor.',
  },
  ...fuelLedgerTr,
  consumption: fuelConsumptionTr,
  rob: fuelRobTr,
  period: fuelPeriodTr,
  analysis: fuelVoyageAnalysisTr,
};
